/**
 * Realtime socket with reconnect, and a 2 s polling fallback for room views when the socket is down.
 * Actions never go over the socket; they stay HTTP (see docs/API.md).
 */
import type { ChatMessage, ReactionEvent, RoomView, WsClientMessage, WsServerMessage } from "@digu/shared";
import { api, isMock } from "./api";
import { countEvent, timing } from "./telemetry";

type RoomCb = (view: RoomView) => void;
type NotifyCb = (m: Extract<WsServerMessage, { t: "notify" }>) => void;
type ChatCb = (roomCode: string, message: ChatMessage) => void;
type ReactionCb = (roomCode: string, reaction: ReactionEvent) => void;
export type SocketState = "connecting" | "open" | "closed";

const POLL_MS = 2000;
/**
 * The server pings every 20 s (apps/api/src/realtime.ts PING_MS). A socket that has heard nothing for 30 s is
 * treated as dead (a wifi-to-mobile switch leaves it half open and `onclose` never comes): drop it, fall back to
 * polling (which shows "Reconnecting" if the network is really gone) and open a new one.
 */
export const SILENT_MS = 30_000;
const WATCH_MS = 5_000;
let lastHeard = 0;
let watchdog: ReturnType<typeof setInterval> | null = null;
const roomSubs = new Map<string, Set<RoomCb>>();
const notifySubs = new Set<NotifyCb>();
const chatSubs = new Set<ChatCb>();
const reactionSubs = new Set<ReactionCb>();
const stateSubs = new Set<(s: SocketState) => void>();
let socket: WebSocket | null = null;
let state: SocketState = "closed";
let attempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let wanted = false;
let lastViews = new Map<string, RoomView>();
/** When the socket last dropped while wanted (D-077: time to get back online). */
let droppedAt: number | null = null;

function setState(s: SocketState): void { if (state !== s) { state = s; stateSubs.forEach((cb) => cb(s)); } }
export const socketState = (): SocketState => state;
export function onSocketState(cb: (s: SocketState) => void): () => void { stateSubs.add(cb); return () => { stateSubs.delete(cb); }; }

function wsUrl(): string {
  const l = window.location;
  return `${l.protocol === "https:" ? "wss" : "ws"}://${l.host}/ws`;
}

function send(m: WsClientMessage): void { if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(m)); }

export function connect(): void {
  wanted = true;
  if (isMock() || typeof WebSocket === "undefined") { setState("closed"); return; }
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  setState("connecting");
  let s: WebSocket;
  try { s = new WebSocket(wsUrl()); } catch { setState("closed"); scheduleReconnect(); return; }
  socket = s;
  const opening = performance.now();
  s.onopen = () => {
    lastHeard = Date.now(); startWatchdog();
    timing("ws-connect", performance.now() - opening);
    if (droppedAt !== null) { timing("ws-reconnect", performance.now() - droppedAt); droppedAt = null; }
    attempts = 0; setState("open"); roomSubs.forEach((_, code) => send({ t: "subscribe", roomCode: code })); };
  s.onmessage = (ev) => {
    lastHeard = Date.now();
    let m: WsServerMessage;
    try { m = JSON.parse(String(ev.data)) as WsServerMessage; } catch { return; }
    switch (m.t) {
      case "ping": send({ t: "pong" }); break;
      case "room": lastViews.set(m.view.code, m.view); roomSubs.get(m.view.code)?.forEach((cb) => cb(m.view)); break;
      case "chat": chatSubs.forEach((cb) => cb(m.roomCode, m.message)); break;
      case "reaction": reactionSubs.forEach((cb) => cb(m.roomCode, m.reaction)); break;
      case "notify": notifySubs.forEach((cb) => cb(m)); break;
    }
  };
  s.onclose = () => {
    if (socket !== s) return; // already dropped by the watchdog
    const wasOpen = state === "open";
    socket = null; stopWatchdog();
    setState("closed");
    if (wasOpen && wanted && droppedAt === null) { droppedAt = performance.now(); countEvent("ws-drop"); }
    if (wanted) scheduleReconnect();
  };
  s.onerror = () => { try { s.close(); } catch { /* already closed */ } };
}

/** Forget the current socket at once (without waiting for a close handshake that may never come) and reconnect. */
function dropSocket(_reason: "silent" | "offline" | "resume"): void {
  const s = socket; if (!s) return;
  socket = null; stopWatchdog();
  s.onopen = null; s.onmessage = null; s.onclose = null; s.onerror = null;
  try { s.close(); } catch { /* half open */ }
  const wasOpen = state === "open";
  setState("closed");
  if (wasOpen && wanted && droppedAt === null) { droppedAt = performance.now(); countEvent("ws-drop"); }
  if (wanted) scheduleReconnect();
}
function startWatchdog(): void {
  if (watchdog) return;
  watchdog = setInterval(() => { if (state === "open" && Date.now() - lastHeard > SILENT_MS) dropSocket("silent"); }, WATCH_MS);
}
function stopWatchdog(): void { if (watchdog) { clearInterval(watchdog); watchdog = null; } }

function scheduleReconnect(): void {
  if (reconnectTimer) return;
  const delay = Math.min(5_000, 500 * 2 ** Math.min(attempts, 4)); attempts += 1;
  reconnectTimer = setTimeout(() => { reconnectTimer = null; if (wanted) connect(); }, delay);
}

/** The page came back (tab shown, network restored, window focused): reconnect now instead of waiting out the backoff. */
function reconnectNow(): void {
  if (!wanted) return;
  attempts = 0;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  // Back from a locked phone or a hidden tab: a socket that went quiet meanwhile is probably dead.
  if (socket && socket.readyState === WebSocket.OPEN && Date.now() - lastHeard > SILENT_MS) { dropSocket("resume"); attempts = 0; if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; } }
  if (socket && socket.readyState === WebSocket.OPEN) { send({ t: "pong" }); return; }
  connect();
}
if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") reconnectNow(); });
  window.addEventListener("online", reconnectNow);
  // The device says the network is gone: don't wait for the watchdog.
  window.addEventListener("offline", () => { if (socket) dropSocket("offline"); });
  window.addEventListener("focus", reconnectNow);
  window.addEventListener("pageshow", reconnectNow);
}

export function disconnect(): void {
  wanted = false;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  stopWatchdog(); socket?.close(); socket = null; setState("closed"); lastViews = new Map();
}

/**
 * Subscribe to a room's view. Delivers the socket's pushes; while the socket is not open it polls
 * GET /rooms/:code every 2 s (which is also the heartbeat). Returns an unsubscribe.
 */
export function subscribeRoom(code: string, cb: RoomCb, opts: { pollMs?: number; onError?: (e: unknown) => void } = {}): () => void {
  let set = roomSubs.get(code);
  if (!set) { set = new Set(); roomSubs.set(code, set); }
  set.add(cb);
  const cached = lastViews.get(code); if (cached) cb(cached);
  connect(); send({ t: "subscribe", roomCode: code });

  let stopped = false; let inFlight = false;
  const poll = async () => {
    if (stopped || inFlight || state === "open") return;
    inFlight = true;
    try { const view = await api.get<RoomView>(`/rooms/${encodeURIComponent(code)}`); if (!stopped) { lastViews.set(code, view); roomSubs.get(code)?.forEach((f) => f(view)); } }
    catch (e) { opts.onError?.(e); }
    finally { inFlight = false; }
  };
  void poll();
  const timer = setInterval(() => { void poll(); }, opts.pollMs ?? POLL_MS);
  const offState = onSocketState((s) => { if (s === "open") send({ t: "subscribe", roomCode: code }); });

  return () => {
    stopped = true; clearInterval(timer); offState();
    const s = roomSubs.get(code); s?.delete(cb); if (s && s.size === 0) roomSubs.delete(code);
  };
}

export function onNotify(cb: NotifyCb): () => void { notifySubs.add(cb); connect(); return () => { notifySubs.delete(cb); }; }
export function onChat(cb: ChatCb): () => void { chatSubs.add(cb); connect(); return () => { chatSubs.delete(cb); }; }
export function onReaction(cb: ReactionCb): () => void { reactionSubs.add(cb); connect(); return () => { reactionSubs.delete(cb); }; }

/** Test hook: feed a server message as if it arrived on the socket. */
export function _injectMessage(m: WsServerMessage): void {
  if (m.t === "room") { lastViews.set(m.view.code, m.view); roomSubs.get(m.view.code)?.forEach((cb) => cb(m.view)); }
  else if (m.t === "notify") notifySubs.forEach((cb) => cb(m));
  else if (m.t === "chat") chatSubs.forEach((cb) => cb(m.roomCode, m.message));
  else if (m.t === "reaction") reactionSubs.forEach((cb) => cb(m.roomCode, m.reaction));
}
